import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Ensure import
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { PriorityInsights } from '@components/interview/analysis-components/ProrityInsight';
import { Summary } from '@components/interview/analysis-components/Summary';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'; // Arc pattern
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlareIcon from '@mui/icons-material/Flare'; // Trigger themes
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'; // Hotspots
import MoodIcon from '@mui/icons-material/Mood'; // Positive
import MoodBadIcon from '@mui/icons-material/MoodBad'; // Negative
import NumbersIcon from '@mui/icons-material/Numbers';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral'; // Neutral
import TimelineIcon from '@mui/icons-material/Timeline'; // Arcs
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { AnalysisLayout } from './layout';

// Helper to get sentiment icon and color
const getSentimentProps = (sentimentType) => {
  switch (sentimentType.toLowerCase()) {
    case 'positive':
      return { icon: <MoodIcon />, color: 'success' };
    case 'neutral':
      return { icon: <SentimentNeutralIcon />, color: 'info' };
    case 'negative':
      return { icon: <MoodBadIcon />, color: 'error' };
    default:
      return { icon: <SentimentNeutralIcon />, color: 'disabled' };
  }
};

// Helper to map intensity to color
const getIntensityColor = (intensity) => {
  switch (intensity?.toLowerCase()) {
    case 'high':
      return 'error';
    case 'moderate':
      return 'warning';
    case 'low':
      return 'info';
    default:
      return 'disabled';
  }
};

// Helper to display arc pattern
const ArcPattern = ({ pattern }) => (
  <Stack
    direction="row"
    spacing={0.5}
    alignItems="center"
    flexWrap="wrap"
    useFlexGap
    sx={{ my: 1 }}
  >
    {pattern.map((step, i) => (
      <React.Fragment key={`${step}-${i}`}>
        <Chip label={step} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
        {i < pattern.length - 1 && <ArrowForwardIcon fontSize="small" color="disabled" />}
      </React.Fragment>
    ))}
  </Stack>
);
ArcPattern.propTypes = {
  pattern: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/** -----------------------------------------------------------------
 * SentimentSpectrumAnalysis – displays portfolio-level Sentiment Spectrum
 * ----------------------------------------------------------------*/
export default function SentimentSpectrumAnalysis({ item }) {
  const [expandedArc, setExpandedArc] = React.useState(false);
  if (!item) return null;

  // --- Start: Inline data handling logic ---
  let results = {};
  if (typeof item.results === 'object' && item.results !== null) {
    results = item.results; // Use directly if already an object
  } else if (typeof item.results === 'string') {
    try {
      results = JSON.parse(item.results); // Try parsing if it's a string
    } catch (err) {
      console.error('Failed to parse results JSON string:', err, 'JSON String:', item.results);
      results = {}; // Assign empty object on parsing error
    }
  } else {
    // Handle cases where results is neither object nor string (e.g., undefined, null)

    console.warn('Results field is not an object or string:', item.results);
  }
  // --- End: Inline data handling logic ---

  // Fallback if results are invalid or missing key fields
  if (
    Object.keys(results).length === 0 ||
    !results.summary ||
    !results.aggregate_sentiment_distribution
  ) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            Analysis Data Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The results for this Sentiment Spectrum analysis could not be loaded or are improperly
            formatted.
          </Typography>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Show Raw Data</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(item, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    );
  }

  const { aggregate_sentiment_distribution: aggSent } = results;
  const totalSentiment =
    (aggSent?.positive || 0) + (aggSent?.neutral || 0) + (aggSent?.negative || 0);

  /* ───────────────── Aggregate Sentiment Distribution Card ──────────────── */
  const SentimentDistribution = () => {
    if (!aggSent || totalSentiment === 0) return null;

    const getPercentage = (value) => (value / totalSentiment) * 100;

    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <TimelineIcon color="primary" /> Aggregate Sentiment Distribution
          </Typography>
          <Box sx={{ width: '100%', mt: 2 }}>
            {Object.entries(aggSent).map(([key, value]) => {
              if (value === undefined || value === null) return null; // Skip if value is missing
              const percentage = getPercentage(value);
              const { icon, color } = getSentimentProps(key);
              return (
                <Box key={key} sx={{ mb: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        textTransform: 'capitalize',
                      }}
                    >
                      {React.cloneElement(icon, { fontSize: 'small', color: color })}
                      {key}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {value} ({percentage.toFixed(1)}%)
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    color={color}
                    sx={{ height: 8, borderRadius: 5 }}
                  />
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>
    );
  };

  /* ───────────────────── Top Emotion Hotspots Card ───────────────────── */
  const EmotionHotspots = () => {
    if (!results.top_emotion_hotspots?.length > 0) return null;
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <LocalFireDepartmentIcon color="error" /> Top Emotion Hotspots
          </Typography>
          <List dense>
            {results.top_emotion_hotspots.map((hotspot, index) => (
              <ListItem
                key={index}
                disableGutters
                sx={{
                  display: 'block',
                  mb: 2,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    {hotspot.emotion}
                  </Typography>
                  <Chip
                    label={hotspot.intensity}
                    size="small"
                    color={getIntensityColor(hotspot.intensity)}
                    variant="outlined"
                    sx={{ textTransform: 'capitalize' }}
                  />
                  <Chip
                    label={`Prevalence: ${hotspot.prevalence}`}
                    size="small"
                    variant="outlined"
                    icon={<NumbersIcon fontSize="inherit" />}
                    sx={{ ml: 'auto !important' }}
                  />
                </Stack>
                <List dense disablePadding>
                  {hotspot.representative_quotes?.map((quote, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemIcon sx={{ minWidth: 20 }}>
                        <FormatQuoteIcon
                          sx={{ fontSize: 16, transform: 'scaleX(-1)', color: 'text.disabled' }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={quote}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontStyle: 'italic',
                          color: 'text.secondary',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  /* ───────────────────── Common Emotional Arcs Accordions ───────────────────── */
  const EmotionalArcs = () => {
    if (!results.common_emotional_arcs?.length > 0) return null;

    const handleChange = (panel) => (event, isExpanded) => {
      setExpandedArc(isExpanded ? panel : false);
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Common Emotional Arcs
        </Typography>
        {results.common_emotional_arcs.map((arc, index) => {
          const panelId = `arc-panel-${index}`;
          return (
            <Accordion
              key={panelId}
              expanded={expandedArc === panelId}
              onChange={handleChange(panelId)}
              sx={{ mb: 1 }}
              variant="outlined"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${panelId}-content`}
                id={`${panelId}-header`}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 } }}
              >
                <TimelineIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ flexShrink: 2, fontWeight: 500 }}>
                  {arc.label}
                </Typography>
                <Chip
                  icon={<NumbersIcon />}
                  label={`Prevalence: ${arc.prevalence}`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                {/* Pattern */}
                {arc.pattern && arc.pattern.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <ArrowForwardIcon fontSize="small" color="action" /> Emotional Pattern
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      <ArcPattern pattern={arc.pattern} />
                    </Box>
                  </Box>
                )}

                {/* Trigger Themes */}
                {arc.trigger_themes?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <FlareIcon fontSize="small" color="secondary" /> Trigger Themes
                    </Typography>
                    <List dense disablePadding sx={{ pl: 3 }}>
                      {arc.trigger_themes.map((theme, i) => (
                        <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ minWidth: 20, mt: 0.5 }}>
                            <FlareIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={theme}
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Representative Quotes */}
                {arc.representative_quotes?.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <FormatQuoteIcon fontSize="small" color="action" /> Representative Quotes
                    </Typography>
                    <List dense disablePadding sx={{ pl: 3 }}>
                      {arc.representative_quotes.map((quote, i) => (
                        <ListItem key={i} disableGutters>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <FormatQuoteIcon
                              sx={{ fontSize: 16, transform: 'scaleX(-1)', color: 'text.disabled' }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={quote}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  /* ──────────────────────────── Render ─────────────────────────── */
  return (
    <AnalysisLayout results={results} item={{ ...item, name: 'Sentiment Spectrum Analysis' }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <MoodIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Sentiment Spectrum Analysis
            {item.analysis_lens_type && (
              <AnalysisLensHelperText lensName={item.analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary item={item} results={results} />
        <SentimentDistribution />
        <EmotionHotspots />
        <EmotionalArcs />
        <PriorityInsights item={item} results={results} />
        <IncludedTranscripts item={item} results={results} />
      </CardContent>
    </AnalysisLayout>
  );
}

SentimentSpectrumAnalysis.propTypes = {
  item: PropTypes.object.isRequired,
};
